import {
  Form,
  Button,
  Listbox,
  OverlayLoader,
  PhoneInput,
} from "@talladega/components";
import { customerValidationDeprecated as customerValidation } from "@talladega/helpers";
import { useTranslation } from "next-i18next";
import { ArrowLeftIcon } from "@talladega/icons";
import { Controller, SubmitHandler, useFormContext } from "react-hook-form";
import { useNumberMask } from "@common/hooks/useMask";
import { usePhoneNumberMask } from "@talladega/hooks";
import { quoteMethods } from "@common/enums/QuoteMethod";
import useCreateAftermarketInstantQuoteMutation, {
  decodeErrorResponse,
} from "@modules/purchase/warranty/hooks/useCreateAftermarketInstantQuote";
import useFeatureFlags from "@modules/feature-flags/hooks/useFeatureFlags";
import { useRouter } from "next/router";
import { useCallback, useState } from "react";
import { FeatureFlags } from "@modules/feature-flags/types/FeatureFlag";
import useUser from "@modules/user/hooks/useUser";
import useProvincesStatusBySourceList from "@modules/purchase/warranty/hooks/useProvincesStatusBySourceList";
import useSourceInfoFromQuery from "@modules/query-string/hooks/useSourceInfoFromQuery";
import { provinceFilters } from "@modules/purchase/warranty/hooks/useEligibleProvinces";
import { TranslationKeys } from "src/@types/i18next";
import { WarrantyInstantQuoteFormProps } from "../types/WarrantyInstantQuoteFormProps";
import {
  quoteGeneratedMode,
  vehicleInfoMode,
  WarrantyInstantQuoteModalMode,
} from "../types/WarrantyInstantQuoteModalMode";
import NoCoveragesModal from "./coverage/NoCoveragesModal";

type Props = {
  onChangeMode(newMode: WarrantyInstantQuoteModalMode): void;
};

const WarrantyInstantQuotePersonalInfo = ({ onChangeMode }: Props) => {
  const { isEnabled } = useFeatureFlags();
  const { t } = useTranslation(["common", "purchase"]);
  const router = useRouter();
  const { locale: rlocale } = router;
  const locale = rlocale || "en";
  const { user } = useUser();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useFormContext<WarrantyInstantQuoteFormProps>();

  const { unMaskPipe: phoneUnMaskPipe } = usePhoneNumberMask();

  const createAftermarketInstantQuoteMutation =
    useCreateAftermarketInstantQuoteMutation();

  // Aftermarket quote alert should show for all users except logged in users and users who have already started a purchase
  const aftermarketQuoteMethodEnabled =
    !user?.isLoggedIn && isEnabled(FeatureFlags.AftermarketQuoteMethod);

  const onBack: () => void = () => {
    onChangeMode(vehicleInfoMode);
  };

  const { source: querySource } = useSourceInfoFromQuery();

  const provincesList = useProvincesStatusBySourceList(
    querySource,
    provinceFilters.isAftermarketEnabled
  );

  const { unMaskPipe: unMaskNumberPipe } = useNumberMask();

  const [showOverlay, setShowOverlay] = useState(false);
  const [showNoCoveragesModal, setShowNoCoveragesModal] = useState(false);

  const sendLeadForm = useCallback(
    (formInput: WarrantyInstantQuoteFormProps) => {
      const {
        firstName: formFirstName,
        lastName: formLastName,
        phone: formPhone,
        email: formEmail,
        province,
        odometer,
        vin,
        vehicleDetails,
        consent,
        quoteMethod,
      } = formInput;

      if (!province || !odometer) {
        return;
      }
      createAftermarketInstantQuoteMutation.mutate(
        {
          firstName: formFirstName,
          lastName: formLastName,
          phone: phoneUnMaskPipe(formPhone),
          email: formEmail,
          province: province.abbr ?? "",
          odometer: unMaskNumberPipe(odometer.toString()),
          vin: vin ?? null,
          year: vehicleDetails?.year ?? null,
          make: vehicleDetails?.make ?? null,
          model: vehicleDetails?.model ?? null,
          trim: vehicleDetails?.trim ?? null,
          isConsentGiven: consent,
          quoteMethod: aftermarketQuoteMethodEnabled ? quoteMethod.id : null,
          fuelType: vehicleDetails ? formInput.fuelType?.name ?? null : null,
          locale,
        },
        {
          onSuccess: (data) => {
            const ratesError =
              data &&
              data.success === false &&
              data.message === decodeErrorResponse.ratesFailed;

            if (aftermarketQuoteMethodEnabled && !ratesError) {
              onChangeMode(quoteGeneratedMode);
              setShowOverlay(false);
            }

            if (ratesError) {
              setShowNoCoveragesModal(true);
              setShowOverlay(false);
            }
          },
          onError: () => {
            setShowOverlay(false);
            setShowNoCoveragesModal(true);
          },
        }
      );
    },
    [
      aftermarketQuoteMethodEnabled,
      createAftermarketInstantQuoteMutation,
      locale,
      onChangeMode,
      phoneUnMaskPipe,
      unMaskNumberPipe,
    ]
  );

  const onFormSubmit: SubmitHandler<WarrantyInstantQuoteFormProps> = (
    formInput: WarrantyInstantQuoteFormProps
  ) => {
    setShowOverlay(true);
    sendLeadForm(formInput);
  };

  return (
    <OverlayLoader isLoading={showOverlay}>
      <div className="mx-auto mb-5 sm:w-4/5 md:w-3/4 lg:w-5/6">
        <div>
          <form onSubmit={handleSubmit(onFormSubmit)}>
            <div className="flex flex-col gap-3">
              <Form.Input
                label={t("common:first_name")}
                maxLength={customerValidation.firstName.maxLength}
                feedback={
                  errors.firstName && (
                    <p className="text-error-500">{errors.firstName.message}</p>
                  )
                }
                {...register("firstName", {
                  required: { value: true, message: t("common:required") },
                  validate: customerValidation.firstName.valid,
                  maxLength: customerValidation.firstName.maxLength,
                })}
              />
              <Form.Input
                label={t("common:last_name")}
                maxLength={customerValidation.lastName.maxLength}
                feedback={
                  errors.lastName && (
                    <p className="text-error-500">{errors.lastName.message}</p>
                  )
                }
                {...register("lastName", {
                  required: { value: true, message: t("common:required") },
                  validate: customerValidation.lastName.valid,
                  maxLength: customerValidation.lastName.maxLength,
                })}
              />

              <p className="text-xs font-semibold">
                {t("common:enter_name_as_displayed_on_license")}
              </p>

              <Form.Input
                label={t("common:email")}
                maxLength={customerValidation.email.maxLength}
                feedback={
                  errors.email && (
                    <p className="text-error-500">{errors.email.message}</p>
                  )
                }
                {...register("email", {
                  required: { value: true, message: t("common:required") },
                  pattern: customerValidation.email.pattern,
                  maxLength: customerValidation.email.maxLength,
                })}
              />
              <PhoneInput
                label={t("common:phone")}
                feedback={errors.phone?.message}
                status={errors.phone ? "error" : "default"}
                {...register("phone", {
                  required: { value: true, message: t("common:required") },
                  validate: {
                    minLength: (value) =>
                      customerValidation.phoneNumber.valid(
                        phoneUnMaskPipe(value)
                      ) || t("common:phone_min_length"),
                    validateAreaCode: (value) =>
                      customerValidation.phoneNumber.validAreaCode(
                        phoneUnMaskPipe(value)
                      ) || t("common:form_error_invalid_phone_number"),
                  },
                })}
              />
              <Controller
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: t("common:province_required"),
                  },
                }}
                name="province"
                render={({ field: { onChange, ref } }) => {
                  return (
                    <Listbox
                      items={provincesList}
                      label={t("common:province")}
                      keyOption={(item) => item.abbr}
                      displayOption={(item) =>
                        item.isEnabled
                          ? t(item.name as TranslationKeys["common"])
                          : `${t(item.name as TranslationKeys["common"])} - ${t(
                              "common:coming_soon"
                            )}`
                      }
                      displayButton={(item) =>
                        item?.name != null
                          ? t(item.name as TranslationKeys["common"])
                          : t("common:select_province")
                      }
                      valueOption={(item) => item}
                      disabledOption={(item) => !item.isEnabled}
                      status={errors.province ? "error" : "default"}
                      feedback={errors.province?.message}
                      onChange={onChange}
                      selectedItem={watch("province")}
                      defaultButtonClassName="text-slate-500"
                      menuPosition="Up"
                      buttonRef={ref}
                    />
                  );
                }}
              />
              <Controller
                name="quoteMethod"
                control={control}
                rules={{ required: true }}
                defaultValue={quoteMethods[0]}
                render={({ field: { onChange, ref } }) => (
                  <Listbox
                    label={t("purchase:send_me_my_quote_via")}
                    onChange={onChange}
                    selectedItem={watch("quoteMethod")}
                    items={quoteMethods}
                    displayOption={(item) => t(item?.name) ?? ""}
                    displayButton={(item) =>
                      item?.name != null
                        ? t(item.name)
                        : t(quoteMethods[0].name)
                    }
                    keyOption={(p) => p.id}
                    valueOption={(p) => p}
                    buttonRef={ref}
                    feedback={
                      errors.quoteMethod?.type === "required" && (
                        <p className="text-error-500">
                          {t("purchase:quote_type_required")}
                        </p>
                      )
                    }
                  />
                )}
              />
            </div>
            <div className="mt-6">
              <div className="mt-6 flex flex-col items-center space-y-4">
                <Button type="submit" size="extra-large">
                  {t("purchase:send_me_my_quote")}
                </Button>
              </div>
            </div>
          </form>
          <div className="mt-6 flex justify-center">
            <Button
              fill="link"
              buttonStyle="borderless"
              spacing="tight-hug"
              leftIcon={<ArrowLeftIcon />}
              className="text-primary-500 underline hover:no-underline"
              onClick={() => onBack()}
            >
              {t("purchase:back_to_vehicle_info")}
            </Button>
          </div>
          {showNoCoveragesModal && (
            <NoCoveragesModal
              showNoCoveragesModal={showNoCoveragesModal}
              setShowNoCoveragesModal={setShowNoCoveragesModal}
              onPrimaryClick={() => setShowNoCoveragesModal(false)}
              primaryButtonText=""
              modalMode="OnlyInquiry"
              userId={user?.id}
            />
          )}
        </div>
      </div>
    </OverlayLoader>
  );
};

export default WarrantyInstantQuotePersonalInfo;
